import matter from "gray-matter";
import { marked } from "marked";
import sanitizeHtml from "sanitize-html";

export const ARTICLE_WORD_COUNT_TARGET = 650;
export const ARTICLE_WORD_COUNT_TOLERANCE = 0.15;
export const ARTICLE_WORD_COUNT_MIN = Math.ceil(ARTICLE_WORD_COUNT_TARGET * (1 - ARTICLE_WORD_COUNT_TOLERANCE));
export const ARTICLE_WORD_COUNT_MAX = Math.floor(ARTICLE_WORD_COUNT_TARGET * (1 + ARTICLE_WORD_COUNT_TOLERANCE));

const SANITIZE_OPTIONS: sanitizeHtml.IOptions = {
  allowedTags: [
    "a",
    "article",
    "aside",
    "blockquote",
    "br",
    "code",
    "em",
    "h1",
    "h2",
    "h3",
    "h4",
    "h5",
    "h6",
    "hr",
    "img",
    "li",
    "ol",
    "p",
    "pre",
    "strong",
    "ul",
    "span",
    "div"
  ],
  allowedAttributes: {
    a: ["href", "title", "target", "rel"],
    img: ["src", "alt", "loading", "width", "height"],
    "*": ["class", "aria-label"]
  },
  allowedSchemes: ["http", "https", "mailto"]
};

export interface ParsedMarkdown<T extends Record<string, unknown>> {
  frontMatter: T;
  body: string;
  excerpt: string;
}

export function parseMarkdown<T extends Record<string, unknown>>(source: string): ParsedMarkdown<T> {
  const parsed = matter(source);
  const body = parsed.content.trim();
  const excerpt = extractExcerpt(body);

  return {
    frontMatter: parsed.data as T,
    body,
    excerpt
  };
}

export function stringifyMarkdown(frontMatter: Record<string, unknown>, body: string): string {
  const cleanFrontMatter = Object.fromEntries(
    Object.entries(frontMatter).filter(([, value]) => value !== undefined)
  );

  return `${matter.stringify(body.trimEnd(), cleanFrontMatter).trimEnd()}\n`;
}

export function renderMarkdown(body: string): string {
  const source = body.replace(/<!--\s*more\s*-->/gi, "");
  const html = marked.parse(source, {
    mangle: false,
    headerIds: false
  }) as string;

  return sanitizeHtml(html, SANITIZE_OPTIONS);
}

export function extractExcerpt(body: string): string {
  const marker = body.split(/<!--\s*more\s*-->/i, 1)[0].trim();
  if (marker) {
    return marker;
  }

  const firstParagraph = body.match(/<p>([\s\S]*?)<\/p>/i);
  if (firstParagraph) {
    return firstParagraph[1].trim();
  }

  return body.trim().slice(0, 240);
}

export function markdownToText(body: string): string {
  return body
    .replace(/<!--\s*more\s*-->/gi, " ")
    .replace(/```[\s\S]*?```/g, " ")
    .replace(/`([^`]+)`/g, "$1")
    .replace(/!\[([^\]]*)\]\([^)]+\)/g, "$1")
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
    .replace(/[#>*_\-]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function countMarkdownWords(body: string): number {
  const text = body
    .replace(/<!--[\s\S]*?-->/g, " ")
    .replace(/```[\s\S]*?```/g, " ")
    .replace(/!\[[^\]]*]\([^)]+\)/g, " ")
    .replace(/\[([^\]]+)]\([^)]+\)/g, "$1")
    .replace(/<[^>]+>/g, " ")
    .replace(/`([^`]+)`/g, "$1");
  return text.match(/[\p{L}\p{N}]+(?:['’\-][\p{L}\p{N}]+)*/gu)?.length || 0;
}

export function isArticleWordCountRecommended(count: number): boolean {
  return count >= ARTICLE_WORD_COUNT_MIN && count <= ARTICLE_WORD_COUNT_MAX;
}

export function summarizeBody(body: string, length = 180): string {
  const text = markdownToText(body);
  if (text.length <= length) {
    return text;
  }

  return `${text.slice(0, length - 1).trimEnd()}…`;
}
